import { queryOptions } from "@tanstack/react-query";
import { POLL_MS, apiGet, apiPost, withQuery } from "@amber/ui";
import {
  activePausesSchema,
  budgetBannerSchema,
  budgetEditResponseSchema,
  budgetPreviewSchema,
  budgetsResponseSchema,
  dependenciesResponseSchema,
  operationsOkResponseSchema,
  pauseResponseSchema,
  pipelineResponseSchema,
  quickActionResponseSchema,
  resumePreviewSchema,
  ruleEditResponseSchema,
  rulePreviewSchema,
  ruleValuesResponseSchema,
  taskDetailSchema,
  taskSummarySchema,
  tasksResponseSchema,
  type BudgetEditRequest,
  type BudgetValues,
  type DependencyKind,
  type PauseRequest,
  type QuickActionRequest,
  type RecordRecoveryRequest,
  type RuleEditRequest,
} from "../contracts/index";
import { operationsKeys } from "./keys";
import type { TasksSearch } from "./search";

export const tasksQuery = (params: TasksSearch) =>
  queryOptions({
    queryKey: operationsKeys.tasks(params),
    queryFn: ({ signal }) => apiGet(withQuery("/api/operations/tasks", { ...params }), tasksResponseSchema, signal),
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });

export const taskQuery = (id: string) =>
  queryOptions({
    queryKey: operationsKeys.task(id),
    queryFn: ({ signal }) => apiGet(`/api/operations/tasks/${encodeURIComponent(id)}`, taskDetailSchema, signal),
    refetchInterval: POLL_MS,
  });

export const taskSummaryQuery = queryOptions({
  queryKey: operationsKeys.tasksSummary,
  queryFn: ({ signal }) => apiGet("/api/operations/tasks/summary", taskSummarySchema, signal),
  refetchInterval: POLL_MS,
});

export const pausesQuery = queryOptions({
  queryKey: operationsKeys.pauses,
  queryFn: ({ signal }) => apiGet("/api/operations/pauses", activePausesSchema, signal),
  refetchInterval: POLL_MS,
});

export const pipelineQuery = queryOptions({
  queryKey: operationsKeys.pipeline,
  queryFn: ({ signal }) => apiGet("/api/operations/pipeline", pipelineResponseSchema, signal),
  refetchInterval: POLL_MS,
});

export const resumePreviewQuery = (id: string) =>
  queryOptions({
    queryKey: operationsKeys.resumePreview(id),
    queryFn: ({ signal }) =>
      apiGet(`/api/operations/pauses/${encodeURIComponent(id)}/resume-preview`, resumePreviewSchema, signal),
  });

export const ruleValuesQuery = queryOptions({
  queryKey: operationsKeys.ruleValues,
  queryFn: ({ signal }) => apiGet("/api/operations/rule-values", ruleValuesResponseSchema, signal),
  refetchInterval: POLL_MS,
});

export const rulePreviewQuery = (rule: string, value: number) =>
  queryOptions({
    queryKey: operationsKeys.rulePreview(rule, value),
    queryFn: ({ signal }) =>
      apiGet(
        withQuery(`/api/operations/rule-values/${encodeURIComponent(rule)}/preview`, { value: String(value) }),
        rulePreviewSchema,
        signal,
      ),
  });

export const budgetsQuery = queryOptions({
  queryKey: operationsKeys.budgets,
  queryFn: ({ signal }) => apiGet("/api/operations/budgets", budgetsResponseSchema, signal),
  refetchInterval: POLL_MS,
});

export const budgetBannerQuery = queryOptions({
  queryKey: operationsKeys.budgetBanner,
  queryFn: ({ signal }) => apiGet("/api/operations/budgets/banner", budgetBannerSchema, signal),
  refetchInterval: POLL_MS,
});

export const budgetPreviewQuery = (values: BudgetValues | null, csrf: string | null) =>
  queryOptions({
    queryKey: operationsKeys.budgetPreview(values),
    queryFn: () => apiPost("/api/operations/budgets/preview", { values }, budgetPreviewSchema, csrf),
    enabled: values !== null,
    placeholderData: (previous) => previous,
  });

export const dependenciesQuery = queryOptions({
  queryKey: operationsKeys.dependencies,
  queryFn: ({ signal }) => apiGet("/api/operations/dependencies", dependenciesResponseSchema, signal),
  refetchInterval: POLL_MS,
});

export const operationsCommands = {
  recordDependencyDate: (
    kind: DependencyKind,
    key: string,
    body: { date: string | null; reason: string; base_revision: number },
    csrf: string | null,
  ) => apiPost(`/api/operations/dependencies/${kind}/${encodeURIComponent(key)}`, body, ruleEditResponseSchema, csrf),
  pause: (body: PauseRequest, csrf: string | null) =>
    apiPost("/api/operations/pauses", body, pauseResponseSchema, csrf),
  resume: (id: string, reason: string, csrf: string | null) =>
    apiPost(`/api/operations/pauses/${encodeURIComponent(id)}/resume`, { reason }, operationsOkResponseSchema, csrf),
  editRule: (rule: string, body: RuleEditRequest, csrf: string | null) =>
    apiPost(`/api/operations/rule-values/${encodeURIComponent(rule)}`, body, ruleEditResponseSchema, csrf),
  editBudgets: (body: BudgetEditRequest, csrf: string | null) =>
    apiPost("/api/operations/budgets", body, budgetEditResponseSchema, csrf),
  quickAction: (taskId: string, action: string, body: QuickActionRequest, csrf: string | null) =>
    apiPost(
      `/api/operations/tasks/${encodeURIComponent(taskId)}/actions/${encodeURIComponent(action)}`,
      body,
      quickActionResponseSchema,
      csrf,
    ),
  recordRecovery: (taskId: string, body: RecordRecoveryRequest, csrf: string | null) =>
    apiPost(`/api/operations/tasks/${encodeURIComponent(taskId)}/recovery`, body, operationsOkResponseSchema, csrf),
};
